import type { I18n, MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import type { StatusLightToken } from '@oranje/ui'

import { labelMap } from '@/shared/lib/i18nLabels'

/**
 * Semáforo del Colaborador: los 12 estados del seed real
 * (`apps/api/prisma/seed.ts`, semáforo WORKER). Un cambio allá obliga a
 * cambiar aquí. Las TRANSICIONES no se declaran a propósito: viajan por la
 * API (`GET /workers/:id/transitions`), ya filtradas por rol.
 *
 * ⚠ Su sitio es `packages/domain`, hoy fuera del alcance acordado.
 */
export const WORKER_STATUSES = [
  'WHITE',
  'APPLE_GREEN',
  'LIGHT_BLUE',
  'ORANGE',
  'STRONG_GREEN',
  'YELLOW',
  'BROWN',
  'PINK',
  'PURPLE',
  'RED',
  'GRAY',
  'BLACK',
] as const

export type WorkerStatus = (typeof WORKER_STATUSES)[number]

/** Qué significa el color EN ESTE semáforo. En otro dice otra cosa. */
/** Lo que dice el chip por cada estado del [[Semáforo del Colaborador]] — nunca el color. */
const WORKER_STATUS_MESSAGE = {
  WHITE: msg`Pre-asignación`,
  APPLE_GREEN: msg`Día 1-2`,
  LIGHT_BLUE: msg`Día 3+`,
  ORANGE: msg`Fijo`,
  STRONG_GREEN: msg`Disponible`,
  YELLOW: msg`Disp. voluntario`,
  BROWN: msg`Asig. temporal`,
  PINK: msg`Stand-by`,
  PURPLE: msg`No regresó`,
  RED: msg`Reportado`,
  GRAY: msg`Accidentado`,
  BLACK: msg`Blacklist`,
}

export const WORKER_STATUS_LABEL: Record<WorkerStatus, string> = labelMap(WORKER_STATUS_MESSAGE)

export const WORKER_STATUS_TOKEN: Record<WorkerStatus, StatusLightToken> = {
  WHITE: 'st-blanco',
  APPLE_GREEN: 'st-verde-manzana',
  LIGHT_BLUE: 'st-azul-claro',
  ORANGE: 'st-naranja',
  STRONG_GREEN: 'st-verde',
  YELLOW: 'st-amarillo',
  BROWN: 'st-cafe',
  PINK: 'st-rosa',
  PURPLE: 'st-morado',
  RED: 'st-rojo',
  GRAY: 'st-gris',
  BLACK: 'st-negro',
}

/**
 * El chip enseña el código Y su significado: `STRONG_GREEN · Disponible`.
 *
 * El código porque es el valor que viaja en la API y el que aparece en los
 * filtros; el significado porque nadie tiene por qué saber de memoria que
 * `BROWN` es una asignación temporal.
 */
export function workerStatusChipLabel(status: WorkerStatus): string {
  /* Regla de Hugo (2026-09-04): el chip NUNCA dice el color — en ningún
     semáforo, ni en dev. El color se VE en el punto/anillo; el texto dice el
     estado en palabras. El código vive en el vault y en la base, no en la UI. */
  return WORKER_STATUS_LABEL[status]
}

/**
 * Por qué el colaborador está en este estado, en una frase. Mismas fichas del
 * Semáforo del Colaborador (vault, fuente de verdad).
 */
const WORKER_STATUS_WHY: Record<WorkerStatus, MessageDescriptor> = {
  WHITE: msg`Subió sus datos en la app y está pendiente de que Reclutamiento valide su expediente.`,
  APPLE_GREEN: msg`Primer o segundo día en el hotel. El Inspector confirma su llegada el día 1.`,
  LIGHT_BLUE: msg`Ponchó en la propiedad al tercer día: ya tiene su uniforme.`,
  ORANGE: msg`Cumplió una semana: colabora fijo en el hotel.`,
  STRONG_GREEN: msg`Disponible para que Reclutamiento lo asigne.`,
  YELLOW: msg`En descanso de un hotel y se ofreció disponible para una asignación temporal.`,
  BROWN: msg`Cubriendo una asignación temporal, completa o parcial.`,
  PINK: msg`El hotel lo mandó a descansar (vacaciones, temporada baja).`,
  PURPLE: msg`No se presentó a trabajar por su cuenta.`,
  RED: msg`El hotel lo reportó. El Inspector está revisando el caso.`,
  GRAY: msg`Tuvo un accidente laboral y está en incapacidad médica. No se le puede vetar mientras esté aquí.`,
  BLACK: msg`La disputa se resolvió a favor del hotel: tiene el acceso bloqueado.`,
}

/**
 * Quién mueve el Semáforo del Colaborador desde cada estado, según las
 * transiciones sembradas (`seed.ts`) y la nota del vault. Compartido entre
 * `ChangeStateDialog` (cuando el rol de quien mira no tiene ningún cambio
 * disponible) y el tooltip del badge en cualquier pantalla.
 */
export const WORKER_STATUS_MOVER: Record<WorkerStatus, MessageDescriptor> = {
  WHITE: msg`lo valida Reclutamiento (la Reclutadora, su Líder de Grupo o el Manager) cuando el expediente está completo.`,
  APPLE_GREEN: msg`lo avanza el sistema con los ponches (el Inspector verifica su llegada).`,
  LIGHT_BLUE: msg`lo avanza el sistema con los ponches; al completar la semana queda Fijo.`,
  ORANGE: msg`el sistema lo libera al terminar la asignación; el hotel puede mandarlo a Stand-by y el propio colaborador ponerse Disponible voluntario.`,
  STRONG_GREEN: msg`lo asigna Reclutamiento (la Reclutadora, su Líder de Grupo o el Manager); el propio colaborador puede ponerse Disponible voluntario.`,
  YELLOW: msg`lo asigna Reclutamiento temporalmente (la Reclutadora, su Líder de Grupo o el Manager).`,
  BROWN: msg`vuelve solo al vencer los días asignados; Reclutamiento puede cancelarlo liberando la asignación.`,
  PINK: msg`lo regresa el hotel (Supervisor, Manager de Área o Manager General); el propio colaborador puede ponerse Disponible voluntario.`,
  PURPLE: msg`vuelve solo cuando el colaborador poncha de nuevo; a la tercera falta el sistema lo manda a Blacklist.`,
  RED: msg`lo resuelve el Inspector: a Disponible o a Blacklist.`,
  GRAY: msg`lo cierra el Inspector con el alta médica.`,
  BLACK: msg`solo el Administrador levanta un veto, desde Blacklist.`,
}

/**
 * El texto completo del tooltip: por qué está aquí y a dónde puede (o no)
 * volver. Un solo lugar para las dos preguntas que hace cualquiera que ve el
 * punto de color sin contexto (Hugo, 2026-10-30).
 */
export function workerStatusTooltip(status: WorkerStatus, i18n: I18n): string {
  return `${i18n._(WORKER_STATUS_WHY[status])} ${i18n._(WORKER_STATUS_MOVER[status])}`
}
